import React from 'react';
import { useTranslation } from 'react-i18next';

import { CitationsSection } from '~/components/citations-section/CitationsSection';
import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { TextSection } from '~/components/text-section/TextSection';
import { ImpactedCompanies } from '~/modules/impacted-companies/ImpactedCompanies';
import { ThematicFocus } from '~/modules/thematic-focus/ThematicFocus';
import { useCatalystInsightDetails } from '~/react-query-hooks/insights/useCatalystInsights';

import { CatalystDrawerHeader } from '../catalyst-drawer-header/CatalystDrawerHeader';
import * as S from './CatalystInsightWidget.styles';

export type CatalystInsightWidgetProps =
  | {
      insightId: string;
      type: 'company';
      entityTag: string;
    }
  | {
      insightId: string;
      type: 'theme';
      themeId: string;
    };

export const CatalystInsightWidget = (props: CatalystInsightWidgetProps) => {
  const { t } = useTranslation(['insights']);
  const { insightId, type } = props;
  const {
    data: catalystInsight,
    isLoading,
    error,
  } = useCatalystInsightDetails(insightId, !!insightId);

  if (error) {
    throw error;
  }

  if (isLoading) {
    return <S.StyledLoader />;
  }

  if (!catalystInsight?.identifier || !catalystInsight) {
    return null;
  }

  return (
    <ErrorBoundaryWrapper>
      <CatalystDrawerHeader
        identifier={catalystInsight.identifier}
        catalystInsight={catalystInsight}
        type={type}
      />
      <S.SectionGroupsWrapper>
        {catalystInsight.section_groups.map(group => (
          <S.GroupWrapper key={group.title} data-testid="section-group-wrapper">
            <S.SectionGroupTitle>{group.title}</S.SectionGroupTitle>
            {group.sections.map(section => (
              <TextSection
                key={section.title}
                title={section.title}
                textContent={section.content}
                variant="secondary"
                useBulletPoints
              />
            ))}
          </S.GroupWrapper>
        ))}
        <div>
          <ThematicFocus
            themes={catalystInsight.themes}
            otherThemes={catalystInsight.other_themes}
            title={t('insights:catalyst.thematicFocus')}
            withPill
          />
        </div>
        <ImpactedCompanies
          impactedCompanies={catalystInsight.impacted_companies}
          variant="secondary"
          title={t('insights:catalyst.mostImpactedCompanies')}
        />
        {!!catalystInsight.citations.length && (
          <S.CitationsSectionWrapper>
            <S.CitationsTitle>
              {t('insights:catalyst.citations')}
            </S.CitationsTitle>
            <CitationsSection
              count={catalystInsight.citations.length}
              items={catalystInsight.citations}
              title={t('insights:catalyst.webLinks')}
            />
          </S.CitationsSectionWrapper>
        )}
      </S.SectionGroupsWrapper>
    </ErrorBoundaryWrapper>
  );
};
